import { useEffect, useMemo, useState } from 'react'
import debounce from 'lodash/debounce'

import Autocomplete from '@mui/material/Autocomplete'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import useGooglePlacesAutocomplete from '@shared/hooks/src/useGooglePlacesAutocomplete'
import { Logger, mergeSlotProps } from '@shared/utils'

const log = Logger('AddressField')

/**
 * Self-contained address field backed by Google Places autocomplete.
 *
 * Controlled like a standard text field — pass `value` and `onChange(event)`.
 * Use `onPlaceSelect` to receive the full structured address when the user
 * picks a Google Places suggestion (or commits a freeSolo clear).
 */
export default function AddressField({
  value = '',
  onChange,
  onPlaceSelect,
  label = 'Address',
  autoComplete,
  required = false,
  disabled = false,
  readOnly = false,
  size,
  error = false,
  helperText,
  name,
  onBlur,
}) {
  const [addressOptions, setAddressOptions] = useState([])
  const [open, setOpen] = useState(false)
  const [focused, setFocused] = useState(false)
  const { getAddressSuggestions, getPlaceDetails } = useGooglePlacesAutocomplete()

  // getAddressSuggestions is stable (useCallback with memoized deps inside
  // the hook), so the debounce survives re-renders.
  const fetchSuggestions = useMemo(
    () =>
      debounce((query) => {
        getAddressSuggestions(query).then((results) => {
          setAddressOptions(query.length ? [query, ...(results ?? [])] : (results ?? []))
        })
      }, 500),
    [getAddressSuggestions]
  )

  useEffect(() => () => fetchSuggestions.cancel(), [fetchSuggestions])

  const handleInputChange = (event, newValue, reason) => {
    // 'reset' fires when the controlled `inputValue` prop changes from
    // outside (e.g. after a formik enableReinitialize, or when the user
    // picks a suggestion). Forwarding that to onChange would dirty the
    // form with a value the parent just set, and a suggestions refetch
    // would waste a Places API call on the full address string.
    if (reason === 'reset') return
    if (reason === 'input') fetchSuggestions(newValue)
    if (!onChange) return
    // 'clear' fires with a null event; synthesize one so formik.handleChange works.
    onChange(event ?? { target: { name, value: newValue } })
  }

  const handleSelect = (_event, option, reason) => {
    if (!onPlaceSelect) return
    // Ignore anything other than an explicit user selection of a Google
    // Places result. We already surface typed/cleared values via the
    // onChange → onInputChange path, so there's nothing else for this
    // handler to do, and acting on null/blur/reset events would wipe
    // formik values set by the caller.
    if (reason !== 'selectOption' || !option?.place_id) return
    getPlaceDetails(option.place_id)
      .then(({ address1: address, city, state, county, postcode }) => {
        const zip = postcode?.split('-')[0] || ''
        onPlaceSelect({ address, city, state, county, zip })
      })
      .catch((e) => log.warn(`getPlaceDetails ${option.place_id}`, e))
  }

  return (
    <Autocomplete
      id={name}
      fullWidth
      includeInputInList
      filterSelectedOptions
      autoComplete
      disableClearable
      forcePopupIcon
      freeSolo
      selectOnFocus={false}
      // Chrome fills an address form whether or not its fields ask to be filled, and a
      // password manager will too. Neither should leave a list of suggestions hanging open
      // over a field nobody is typing in.
      open={open && focused}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      noOptionsText="No matches"
      disabled={disabled}
      readOnly={readOnly}
      inputValue={value}
      options={addressOptions}
      filterOptions={(x) => x}
      getOptionLabel={(option) => (typeof option === 'string' ? option : (option.description ?? ''))}
      onInputChange={handleInputChange}
      onChange={handleSelect}
      renderOption={({ key, ...props }, option) => {
        const isGooglePlace = option.place_id
        return (
          <li key={`${key}${isGooglePlace ? '' : '_input'}`} {...props}>
            {isGooglePlace ? (
              <Stack>
                <Typography component="span">{option.structured_formatting.main_text}</Typography>
                <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" component="span">
                {option}
              </Typography>
            )}
          </li>
        )
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          size={size}
          name={name}
          error={error}
          helperText={helperText}
          onFocus={() => setFocused(true)}
          onBlur={(event) => {
            setFocused(false)
            onBlur?.(event)
          }}
          // Autocomplete turns the browser's own autofill off. Chrome fills an address form
          // anyway, so the field says what it holds and gets filled accurately.
          slotProps={autoComplete ? mergeSlotProps(params.slotProps, { htmlInput: { autoComplete } }) : params.slotProps}
        />
      )}
    />
  )
}
